/**
 * features/finance/Housekeeping.tsx
 * ------------------------------------------------------------------
 * ARE THE NUMBERED SERIES INTACT?
 *
 * Ten series now run through this system. Every number is allocated
 * inside the transaction that writes its document, so a failed write
 * cannot burn one. That is the design; this is the check that it held.
 *
 * "WHERE IS PV/2026/0184?" is the first question an auditor asks about a
 * series, and there are two answers: it was cancelled and is still on
 * file, or nobody knows. The second turns a query into a finding,
 * because a missing document in a payment series is indistinguishable
 * from one that was removed.
 *
 * IT REPORTS AND DOES NOT REPAIR. Renumbering to close a gap rewrites
 * documents people already hold, which is worse than the gap. The one
 * thing offered is nudging a counter that has fallen BEHIND its
 * documents — because that is about to reissue a number that is already
 * in use, and duplicates are the only failure here that cannot be
 * untangled afterwards.
 * ------------------------------------------------------------------ */
import { useEffect, useState } from 'react';
import { collection, doc, getDocs, onSnapshot, query } from 'firebase/firestore';
import {
  Wrench, ShieldCheck, AlertTriangle, TriangleAlert, Info, RefreshCw,
} from 'lucide-react';
import { Spinner } from '@/components/ui/primitives';
import { db, setDoc } from '@/lib/firebase';
import { FINANCE_COLLECTIONS } from '@/lib/financeCore';
import { GL_COLLECTIONS, journalCounterId } from '@/lib/gl';
import { VOUCHER_COLLECTIONS, voucherCounterId } from '@/lib/vouchers';
import { PO_COLLECTIONS, poCounterId, grnCounterId } from '@/lib/purchaseOrders';
import { REQ_COLLECTIONS, reqCounterId } from '@/lib/requisitions';
import { BATCH_COLLECTION, batchCounterId } from '@/lib/paymentBatches';
import { PETTY_COLLECTIONS, pettySlipCounterId } from '@/lib/pettyCash';
import { PARTY_JOURNAL_COLLECTION, partyCounterId } from '@/lib/partyJournals';
import { adjustmentCounterId } from '@/lib/adjustments';
import {
  checkSeries, rankFindings, summarise, seqOf,
  type SeriesFinding, type SeriesSpec,
} from '@/lib/housekeeping';
import {
  PageHeader, SoftCard, PanelHeader, StatChip, glassFieldClass, btnGhost,
} from '@/features/finance/ui';

const COUNTERS = 'financeCounters';

/**
 * Every series, where its counter lives and where its documents do.
 *
 * One table rather than ten bespoke checks: a series added next year is
 * a row here, and a series NOT listed here is one nobody is checking —
 * which is the failure this table exists to make visible.
 */
function specsFor(year: string, fy: string): (SeriesSpec & {
  collection: string; field: string;
})[] {
  return [
    { key: 'journals', label: 'Journals', counterId: journalCounterId(fy),
      sample: 'jnl_2026_0001', collection: GL_COLLECTIONS.journals, field: 'journalNo' },
    { key: 'invoices', label: 'Invoices', counterId: `invoices-${year}`,
      sample: 'INV/2026/0001', collection: FINANCE_COLLECTIONS.invoices, field: 'invoiceNo' },
    { key: 'adjustments', label: 'Credit notes & write-offs', counterId: adjustmentCounterId(year),
      sample: 'ADJ/2026/0001', collection: FINANCE_COLLECTIONS.adjustments, field: 'adjustmentNo' },
    { key: 'vouchers', label: 'Payment vouchers', counterId: voucherCounterId(fy),
      sample: 'PV/2026/0001', collection: VOUCHER_COLLECTIONS.vouchers, field: 'voucherNo' },
    { key: 'batches', label: 'Payment runs', counterId: batchCounterId(year),
      sample: 'PB/2026/0001', collection: BATCH_COLLECTION, field: 'batchNo' },
    { key: 'purchaseOrders', label: 'Purchase orders', counterId: poCounterId(year),
      sample: 'PO/2026/0001', collection: PO_COLLECTIONS.orders, field: 'poNo' },
    { key: 'goodsReceived', label: 'Goods received notes', counterId: grnCounterId(year),
      sample: 'GRN/2026/0001', collection: PO_COLLECTIONS.receipts, field: 'grnNo' },
    { key: 'requisitions', label: 'Requisitions', counterId: reqCounterId(year),
      sample: 'REQ/2026/0001', collection: REQ_COLLECTIONS.requisitions, field: 'reqNo' },
    { key: 'pettySlips', label: 'Petty cash slips', counterId: pettySlipCounterId(year),
      sample: 'PC/2026/0001', collection: PETTY_COLLECTIONS.slips, field: 'slipNo' },
    { key: 'partyJournals', label: 'Debtor & creditor journals', counterId: partyCounterId(year),
      sample: 'DJ/2026/0001', collection: PARTY_JOURNAL_COLLECTION, field: 'journalRef' },
  ];
}

export default function Housekeeping() {
  const thisYear = String(new Date().getFullYear());
  const [year, setYear] = useState(thisYear);
  const [findings, setFindings] = useState<SeriesFinding[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [counters, setCounters] = useState<Record<string, number>>({});

  useEffect(() => onSnapshot(
    query(collection(db, COUNTERS)),
    (s) => setCounters(Object.fromEntries(
      s.docs.map((d) => [d.id, Number(d.data()?.seq) || 0]),
    )),
    () => setCounters({}),
  ), []);

  useEffect(() => {
    let cancelled = false;
    setFindings(null);

    (async () => {
      const specs = specsFor(year, `FY${year}`);
      const out: SeriesFinding[] = [];

      for (const spec of specs) {
        let used: number[] = [];
        try {
          const snap = await getDocs(query(collection(db, spec.collection)));
          used = snap.docs
            /* Only this year's. A series is per-year, and folding every
               year together would report last January as a gap in this
               one for the rest of the school's life. */
            .map((d) => String((d.data() as Record<string, unknown>)[spec.field] ?? ''))
            .filter((ref) => ref.includes(year))
            .map(seqOf)
            .filter((n): n is number => n !== null);
        } catch {
          /* A collection this user cannot read is not a finding about
             the numbering. Skipped rather than reported as empty, which
             would read as "every number is missing". */
          continue;
        }
        out.push(checkSeries({ spec, counter: counters[spec.counterId] ?? 0, used }));
      }

      if (!cancelled) setFindings(rankFindings(out));
    })();

    return () => { cancelled = true; };
  }, [year, counters]);

  async function nudgeCounter(f: SeriesFinding) {
    const spec = specsFor(year, `FY${year}`).find((s) => s.key === f.key);
    if (!spec) return;
    if (!window.confirm(
      `Set the ${f.label} counter to ${f.highest}?\n\n`
      + 'This stops the next document reusing a number already in use. It does not '
      + 'change any existing document.',
    )) return;

    setBusy(f.key);
    try {
      await setDoc(doc(db, COUNTERS, spec.counterId),
        { seq: f.highest, updatedAt: new Date().toISOString() }, { merge: true });
    } finally {
      setBusy(null);
    }
  }

  if (findings === null) return <div className="p-6 flex justify-center"><Spinner /></div>;

  const s = summarise(findings);

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      <PageHeader
        icon={Wrench} tone="navy"
        title="Housekeeping"
        subtitle="Whether every numbered series is intact, and nothing has quietly gone missing"
        actions={(
          <select value={year} onChange={(e) => setYear(e.target.value)}
            className={glassFieldClass('max-w-[120px] text-sm')}>
            {[0, 1, 2].map((n) => {
              const y = String(Number(thisYear) - n);
              return <option key={y} value={y}>{y}</option>;
            })}
          </select>
        )}
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        <StatChip tone="slate" value={String(s.total)} label="Series checked" />
        <StatChip tone={s.allClear ? 'emerald' : 'amber'}
          icon={s.allClear ? ShieldCheck : AlertTriangle}
          value={String(s.needsAttention)} label="Need attention" />
        <StatChip tone={findings.some((f) => f.verdict === 'duplicates' || f.verdict === 'behind')
          ? 'rose' : 'slate'}
          value={String(findings.filter((f) => f.verdict === 'duplicates' || f.verdict === 'behind').length)}
          label="Could reissue a number" />
      </div>

      <SoftCard pad="p-0">
        <PanelHeader icon={Wrench} tone="navy" title="Numbered series"
          subtitle="Most serious first — a duplicate outranks a gap by a long way" />

        <div className="divide-y divide-gray-50 dark:divide-gray-800">
          {findings.map((f) => (
            <div key={f.key} className="px-4 py-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-gray-900 dark:text-white flex-1 min-w-[160px]">
                  {f.label}
                </span>
                <span className="text-[12px] text-gray-400 tabular-nums">
                  {f.count} issued · counter {f.counter}
                </span>
                <span className={`text-[11px] px-2 py-0.5 rounded font-medium ${
                  f.verdict === 'duplicates' || f.verdict === 'behind'
                    ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200'
                    : f.verdict === 'gaps'
                      ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
                      : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
                }`}>
                  {f.verdict === 'sound' ? 'Intact'
                    : f.verdict === 'unused' ? 'Unused'
                      : f.verdict === 'gaps' ? `${f.gaps.length} gap${f.gaps.length === 1 ? '' : 's'}`
                        : f.verdict === 'behind' ? 'Counter behind'
                          : 'Duplicates'}
                </span>

                {/* The one repair offered, and only for the case that is
                    about to do damage. */}
                {f.verdict === 'behind' && (
                  <button type="button" className={btnGhost} disabled={busy === f.key}
                    onClick={() => nudgeCounter(f)}>
                    <RefreshCw size={13} /> Set counter to {f.highest}
                  </button>
                )}
              </div>
              <p className={`text-[12px] mt-1 ${
                f.verdict === 'duplicates' || f.verdict === 'behind'
                  ? 'text-red-700 dark:text-red-300' : 'text-gray-500'
              }`}>
                {f.message}
              </p>
            </div>
          ))}
        </div>

        <p className="px-4 py-3 border-t border-gray-100 dark:border-gray-700 text-[12px] text-gray-500 flex items-start gap-1.5">
          <Info size={13} className="mt-0.5 shrink-0" />
          Gaps are reported and never repaired: renumbering to close one would rewrite
          documents people already hold, which is worse than the gap. A gap is usually a
          write that rolled back after taking its number.
        </p>
      </SoftCard>

      {findings.some((f) => f.verdict === 'duplicates') && (
        <SoftCard className="mt-4 border-l-4 border-red-400">
          <p className="text-sm text-red-700 dark:text-red-300 flex items-start gap-2">
            <TriangleAlert size={15} className="mt-0.5 shrink-0" />
            A duplicated number is the one failure here that cannot be untangled later — two
            documents carrying it cannot be told apart in any report, statement or audit
            trail. This needs a person before anything else on the page.
          </p>
        </SoftCard>
      )}
    </div>
  );
}
